import * as React from 'react';
import {style} from 'typestyle';
import {percent, px} from 'csx';
import {colorBrand, colorSubtle} from '../theme/color';
import {centerCenter, endJustified, horizontal} from 'csstips';
import {RatecardSchema, RatecardStatisticsSchema} from '../types/schema';

export interface RatecardGaugeProps {
  ratecard: RatecardSchema;
  statistics: RatecardStatisticsSchema;
  className?: string;
  i18n?: {
    views(views: number): string;
    videos(videos: number): string;
  }
}

export interface RatecardGaugeState {
  mounted: boolean;
}

const RATES = [
  { from: 0, to: 50000, label: '50k' },
  { from: 50001, to: 100000, label: '100k' },
  { from: 100001, to: 500000, label: '500k' },
  { from: 500001, to: 1000000, label: '1M' },
  { from: 1000001, to: 90000000, label: '∞' },
];

const DEFAULT_I18N = {
  views: (views: number) => `${views.toLocaleString()} average video views`,
  videos: (views: number, videos: number) => `${views.toLocaleString()} views / ${videos.toLocaleString()} ${videos === 1 ? 'video' : 'videos'}`,
};

export class RatecardGauge extends React.Component<RatecardGaugeProps, RatecardGaugeState> {
  private _isMounted: boolean;

  render() {
    if (!this.props.statistics) {
      return null;
    }

    const { i18n = DEFAULT_I18N } = this.props;
    const { video_count, average_views, view_count } = this.props.statistics;
    const { mounted } = this.state;

    const rateIndex = RATES.findIndex(r => average_views >= r.from && average_views <= r.to);
    const rate = RATES[rateIndex];
    const prev = RATES[rateIndex - 1];
    const progress = (!rate || !mounted) ? 0 : 100 * (average_views - rate.from) / (rate.to - rate.from);

    return (
      <div className={this.props.className}>
        <div className={RatecardGauge.styles.bar}>
          <div style={{ width: percent(progress) }} />
        </div>
        <div className={RatecardGauge.styles.labels}>
          <div>{rate ? rate.label : '∞'}</div>
        </div>
        <div className={RatecardGauge.styles.views}>{i18n.views(average_views)}</div>
        <div className={RatecardGauge.styles.period}>{i18n.videos(view_count, video_count)}</div>
      </div>
    )
  }

  constructor(props) {
    // todo beginning of ratecard number
    super(props);
    this.state = { mounted: false };
  }

  componentDidMount() {
    this._isMounted = true;
    setTimeout(() => this._isMounted && this.setState({ mounted: true }), 42);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  static styles = {
    views: style({
      ...centerCenter,
      fontSize: px(18),
      lineHeight: px(24),
      fontWeight: 'bold'
    }),
    period: style({
      ...centerCenter,
      color: colorSubtle.toString(),
      fontSize: px(14),
      lineHeight: px(24)
    }),
    bar: style({
      position: 'relative',
      height: px(16),
      width: 'calc(100% - 40px)',
      borderRadius: px(50),
      margin: '0 20px',
      border: `1px solid ${colorSubtle.fade(0.7).toString()}`,
      $nest: {
        '&> div': {
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          borderRadius: px(50),
          minWidth: px(15),
          transition: 'width 0.4s ease-in',
          background: colorBrand.toString()
        }
      }
    }),
    labels: style({
      ...horizontal,
      ...endJustified,
      margin: '5px 0 0 0',
      $nest: {
        '&> div': {
          ...centerCenter,
          width: px(40),
          color: colorSubtle.toString(),
          fontSize: px(14),
          lineHeight: px(24)
        }
      }
    })
  }
}